import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../../../cn.ts";
import { describedBy, FieldFrame } from "../Field/Field.tsx";

/**
 * § Primitives, Input: prefix and suffix slots, the invalid state setting `aria-invalid` and
 * `aria-describedby`, the visible label rather than a placeholder label (FR-015-091,
 * FR-015-100). Taken from shadcn/ui's Input, the field Spectrum UI's Floating Label Input is
 * built on, and from shadcn/ui's Input Group for the prefix and suffix, themed through the
 * variables of `packages/tokens` (2026-09-15, by the owner's decision).
 *
 * What differs from the source, and why: the height reads the density's control heights
 * rather than `h-9`; the 3 px ring at half the ring colour is not carried, because it falls
 * below the 3:1 of § Focus ring and the one `:focus-visible` rule of `theme/focus.css` draws
 * the 2 px ring on the input, or on the group around it when there is a prefix or suffix;
 * read-only takes the muted fill, a state the source has no class for; and the group's rules
 * for a button or a key cap inside an addon are not carried, because Baton's slots hold an
 * icon or a short text.
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix" | "children"> {
  readonly label: ReactNode;
  readonly hideLabel?: boolean;
  readonly size?: "sm" | "md" | "lg";
  readonly prefix?: ReactNode;
  readonly suffix?: ReactNode;
  readonly invalid?: boolean;
  readonly message?: ReactNode;
  readonly describedBy?: string;
  readonly ref?: Ref<HTMLInputElement>;
}

const heights = { sm: "h-(--control-height-sm)", md: "h-(--control-height-md)", lg: "h-(--control-height-lg)" } as const;

/**
 * The input, its group and its addon classes, shared with the QueryBar of § Navigation, which is an input group around a combobox.
 * A field draws no fill in either theme: shadcn's dark `bg-input/30`, the border colour at 30%, blended to #383838 on the #272727
 * sheet, where the secondary text of an addon, the filter button and a placeholder read 3.91:1 (2026-09-29, the dark axe sweep).
 */
export const inputClasses =
  "w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground read-only:bg-muted disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40";

export const groupClasses =
  "group/input-group relative flex w-full min-w-0 items-center rounded-md border border-input shadow-xs transition-[color,box-shadow] has-[>[data-align=inline-start]]:[&>input]:pl-2 has-[>[data-align=inline-end]]:[&>input]:pr-2 has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-destructive/20 dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40 has-[input:read-only]:bg-muted has-[textarea:read-only]:bg-muted data-[disabled=true]:opacity-50";

export const addonClasses = "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none [&>svg:not([class*='size-'])]:size-4";

export function Input({ label, hideLabel, size = "md", prefix, suffix, invalid = false, message, describedBy: external, disabled, readOnly, required, className, id: givenId, ref, ...rest }: InputProps) {
  const generated = useId();
  const id = givenId ?? generated;
  const messageId = `${id}-message`;
  const grouped = prefix !== undefined || suffix !== undefined;
  const input = (
    <input
      ref={ref}
      id={id}
      data-slot="input"
      className={grouped ? cn(inputClasses, "h-full flex-1 rounded-none border-0 bg-transparent shadow-none dark:bg-transparent") : cn(inputClasses, heights[size])}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy(messageId, message !== undefined && message !== null, external)}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      {...rest}
    />
  );
  return (
    <FieldFrame id={id} label={label} {...(hideLabel === undefined ? {} : { hideLabel })} {...(required === undefined ? {} : { required })} message={message} invalid={invalid} messageId={messageId} {...(className === undefined ? {} : { className })}>
      {grouped ? (
        <div data-slot="input-group" role="group" data-disabled={disabled ? "true" : undefined} className={cn(groupClasses, heights[size])}>
          {prefix !== undefined && (
            <span data-slot="input-group-addon" data-align="inline-start" className={cn(addonClasses, "order-first pl-3")}>
              {prefix}
            </span>
          )}
          {input}
          {suffix !== undefined && (
            <span data-slot="input-group-addon" data-align="inline-end" className={cn(addonClasses, "order-last pr-3")}>
              {suffix}
            </span>
          )}
        </div>
      ) : (
        input
      )}
    </FieldFrame>
  );
}
